import { z } from 'zod';
import type { DefaultValues } from 'react-hook-form';
import {
    additionalVehicleDetailsSchema,
    LAST_MOT_DATE_UNREADABLE,
    LAST_SERVICE_DATE_UNREADABLE,
    normaliseVin,
    normaliseVrm,
    type AdditionalVehicleDetailsFormInputs,
} from '../../schemas/additionalvehicledetails.schema';
import type { Vehicle } from '../../types/vinvrmsearch.types';
import type { VehicleDerivative } from '../../types/manualsearch.types';
import { isVehicle } from '../..';
import { parseSeedDate } from '../../utils/parseSeedDate';

// Issue #859 review follow-up — authored copy for Zod's own untranslated `invalid_date`
// issue, keyed by field name and applied at `fieldIssues` construction time (below) by
// matching the STABLE `issue.code`, never by comparing against Zod's display text. A
// string comparison against "Invalid date" was tried first and rejected on review: a
// zod bump or an added `invalid_type_error` changes the display text, the comparison
// silently stops matching, and the raw Zod string reappears with nobody the wiser --
// exactly the class of silent degradation #859 exists to close, only moved rather than
// fixed. Mapping on `issue.code` means a text change cannot affect this at all.
//
// `registrationDate` is deliberately ABSENT from this map. Its own Invalid-Date handling
// stays value-driven in its Controller (`RegistrationDateField.tsx`) (the `unreadable` predicate, paired with
// `REGISTRATION_DATE_UNREADABLE`) so its `role` attribute keeps being driven by RHF's
// `fieldState.error` rather than by `fieldIssues` -- see that Controller's docblock and
// #823.
export const INVALID_DATE_COPY: Partial<Record<keyof AdditionalVehicleDetailsFormInputs, string>> = {
    lastMotDate: LAST_MOT_DATE_UNREADABLE,
    lastServiceDate: LAST_SERVICE_DATE_UNREADABLE,
};

/**
 * Issue #825 review follow-up — the ONLY Zod issue codes that may raise the upward
 * `onRegistrationDateBlockedChange` signal. They are the quote journey's step-gate
 * precondition, and nothing else: the registration date is ABSENT or UNREADABLE.
 *
 * The reporter below was `!!fieldIssues.registrationDate` — any issue whose `path[0]` is
 * `registrationDate`. As of #1233 the schema DOES carry an object-level `superRefine`; it
 * reports only on `lastServiceMileage`/`lastServiceDate`, never on `registrationDate`, so
 * the narrowing below is what keeps this gate correct now that one exists.
 * `registrationDate` itself is still `z.date({ required_error, invalid_type_error })` with
 * no further checks, so the only codes it can raise are `invalid_type` (undefined, null,
 * or a non-Date) and `invalid_date` (a truthy `Invalid Date` from an unparseable stored
 * string) — both of which ARE the precondition. Verified by probe against the installed
 * zod 3.25.75; note v3 has no separate `required` code, an absent key is `invalid_type`
 * with `received: 'undefined'`. So this narrowing excludes nothing that blocks today.
 *
 * What it closes is forward: a future `.superRefine()` or cross-field rule reporting
 * `path: ['registrationDate']` — "registered before the model year", say — would have
 * started feeding the step gate, which blocks BOTH `Next` on the vehicle step and
 * `Complete Quote` from every later step (QuoteContext.tsx `stepBlockReason`), under copy
 * that says the quote needs a first registration date (`VEHICLE_NEEDS_REGISTRATION_DATE`).
 * The user would be told to supply a date that is already on screen, and the D3a recovery
 * sentence — clear the vehicle and select it again — would reproduce the same state. That
 * is the stranding risk D3a/R11 exists to prevent, and it breaches D2/D3's boundary: the
 * gate depends on `registrationDate` alone, and only as quote-service's write precondition
 * (StoreQuoteRequest.php:225).
 *
 * A future FIELD-LEVEL constraint is excluded by the same rule, deliberately. A
 * `.min()`/`.max()` date bound raises `too_small`/`too_big` for a date that is present and
 * readable: that correctly pauses auto-save and shows a message, but it is not the write
 * precondition and must not gate the journey. Anything — field-level or object-level —
 * that should ALSO gate the journey has to be added to this set explicitly, with a test.
 * Pinned by AdditionalVehicleDetailsForm.gate-boundary.test.tsx.
 */
export const REGISTRATION_DATE_PRECONDITION_CODES: ReadonlySet<z.ZodIssueCode> = new Set([
    z.ZodIssueCode.invalid_type,
    z.ZodIssueCode.invalid_date,
]);

export interface SeededVehicleDetails {
    last_mot_date?: string | null;
    average_annual_mileage?: number | null;
    current_estimated_mileage?: number | null;
}

// `id` is included so two vehicles that both lack vin and vrm cannot collide on
// `v:|` and defeat the vehicle-switch reset in `useAdditionalVehicleDetailsFormState.ts`.
export function buildVehicleIdentity(selectedVehicle: Vehicle | VehicleDerivative | undefined): string {
    return selectedVehicle
        ? isVehicle(selectedVehicle)
            ? `v:${selectedVehicle.id ?? ''}|${selectedVehicle.vin ?? ''}|${selectedVehicle.current_status?.vrm ?? ''}`
            : `d:${selectedVehicle.id}`
        : 'none';
}

// Generate default values from the selected vehicle
// #807 -- was `(): AdditionalVehicleDetailsFormInputs`. registrationDate is now
// required in the inferred type, so the `registrationDate: undefined` branches
// below stop compiling (TS2322) under that annotation. useForm's defaultValues
// accepts DefaultValues<T>, a deep partial, so widening to that instead is the
// correct and minimal fix -- same input/output split DealerForm.tsx:12, 46, 69
// already uses. The undefined values themselves are unchanged: nothing may seed
// a date the user has not supplied.
export function buildDefaultValues(
    selectedVehicle: Vehicle | VehicleDerivative | undefined,
    seededDetails: SeededVehicleDetails | null | undefined,
    prePopulateMileage: boolean,
): DefaultValues<AdditionalVehicleDetailsFormInputs> {
    const estimate = seededDetails?.current_estimated_mileage;
    const hasEstimate = typeof estimate === 'number';
    const annualEstimate = seededDetails?.average_annual_mileage;
    const hasAnnualEstimate = typeof annualEstimate === 'number';

    if (!selectedVehicle) {
        return {
            vin: undefined,
            vrm: undefined,
            mileage: hasEstimate
                ? (prePopulateMileage ? Math.round(estimate as number) : null)
                : undefined,
            annualMileage: hasAnnualEstimate
                ? (prePopulateMileage ? Math.round(annualEstimate as number) : null)
                : undefined,
            registrationDate: undefined,
            lastMotDate: undefined,
            lastServiceDate: undefined,
            lastServiceMileage: undefined,
        };
    }

    // If it's a Vehicle, extract existing data
    if (isVehicle(selectedVehicle)) {
        return {
            // Issue #859 — normalise the SEED, not the schema (D4). A stored
            // "AB12 CDE" / lower-case value becomes valid on seed, so `isValid`
            // is immediately true and the next auto-submit writes the canonical
            // form back — the stored document repairs itself with no user action.
            vin: normaliseVin(selectedVehicle.vin),
            vrm: normaliseVrm(selectedVehicle.current_status?.vrm),
            // A stored reading wins over the estimate seed. `null` is the signal
            // mergeOdometerReading reads as "preserve the stored reading"; `undefined` clears.
            mileage: selectedVehicle.current_status?.mileage != null
                ? selectedVehicle.current_status.mileage
                : hasEstimate
                    ? (prePopulateMileage ? Math.round(estimate as number) : null)
                    : undefined,
            // Same precedence and the same null/undefined sentinel as mileage above.
            annualMileage: selectedVehicle.current_status?.annual_mileage != null
                ? selectedVehicle.current_status.annual_mileage
                : hasAnnualEstimate
                    ? (prePopulateMileage ? Math.round(annualEstimate as number) : null)
                    : undefined,
            registrationDate: parseSeedDate(selectedVehicle.date_of_first_registration),
            lastMotDate:
                parseSeedDate(seededDetails?.last_mot_date) ?? parseSeedDate(selectedVehicle.last_mot_date),
            lastServiceDate: parseSeedDate(selectedVehicle.last_service_date),
            lastServiceMileage: selectedVehicle.last_service_mileage ?? undefined,
        };
    }

    // If it's a VehicleDerivative, start with empty values (seeded values applied where available)
    return {
        vin: undefined,
        vrm: undefined,
        mileage: hasEstimate
            ? (prePopulateMileage ? Math.round(estimate as number) : null)
            : undefined,
        annualMileage: hasAnnualEstimate
            ? (prePopulateMileage ? Math.round(annualEstimate as number) : null)
            : undefined,
        registrationDate: undefined,
        lastMotDate: parseSeedDate(seededDetails?.last_mot_date),
        lastServiceDate: undefined,
        lastServiceMileage: undefined,
    };
}

// The flag joins the identity so late dealer config still fills an untouched field, but only
// when an estimate exists, since otherwise it cannot change any default.
export function buildResetIdentity(
    vehicleIdentity: string,
    seededDetails: SeededVehicleDetails | null | undefined,
    prePopulateMileage: boolean,
): string {
    const hasEstimate = typeof seededDetails?.current_estimated_mileage === 'number';
    const hasAnnualEstimate = typeof seededDetails?.average_annual_mileage === 'number';
    const flagAffectsDefaults = prePopulateMileage && (hasEstimate || hasAnnualEstimate);

    return [
        vehicleIdentity,
        seededDetails?.current_estimated_mileage ?? '',
        seededDetails?.average_annual_mileage ?? '',
        seededDetails?.last_mot_date ?? '',
        flagAffectsDefaults ? '1' : '0',
    ].join('::');
}

export interface DerivedIssues {
    fieldIssues: Partial<Record<keyof AdditionalVehicleDetailsFormInputs, string>>;
    formIssues: string[];
    registrationDateBlocked: boolean;
}

export function deriveIssues(watchedValues: AdditionalVehicleDetailsFormInputs): DerivedIssues {
    const result = additionalVehicleDetailsSchema.safeParse(watchedValues);
    if (result.success) {
        return { fieldIssues: {} as Partial<Record<keyof AdditionalVehicleDetailsFormInputs, string>>, formIssues: [] as string[], registrationDateBlocked: false };
    }

    const fields: Partial<Record<keyof AdditionalVehicleDetailsFormInputs, string>> = {};
    const form: string[] = [];
    // Issue #825, D3, narrowed on review. The step-gate signal is derived HERE, off
    // the same single parse, because it needs `issue.code` and the message map above
    // necessarily discards it. This is an ADDITIONAL read of `result.error.issues` —
    // `fields`/`form`, and therefore every message #859 puts on screen, are built
    // exactly as before and are not affected by this.
    let registrationDatePreconditionUnmet = false;

    for (const issue of result.error.issues) {
        const key = issue.path[0];

        // A zero-length path is a whole-object refinement. The #1233 superRefine
        // always reports a field path, so this branch remains unreached; bucketing
        // into formIssues rather than dropping means a future one cannot silently
        // reintroduce an unexplained blocked state.
        if (typeof key !== 'string') {
            form.push(issue.message);
            continue;
        }

        // Issue #859 review follow-up — substitute authored copy for Zod's own
        // untranslated `invalid_date` message HERE, at construction time, keyed on
        // the stable `issue.code` rather than `issue.message` text. See
        // `INVALID_DATE_COPY`'s docblock above for why this moved out of the render.
        const fieldKey = key as keyof AdditionalVehicleDetailsFormInputs;
        const message = issue.code === 'invalid_date' ? (INVALID_DATE_COPY[fieldKey] ?? issue.message) : issue.message;

        // Issue #825 review follow-up — the step gate reads the ISSUE CLASS, not the
        // presence of a message. Scanning every issue rather than stopping at the
        // first one per field is deliberate: `fields[fieldKey] ??= message` keeps only
        // the first, so a precondition issue reported after a non-precondition one on
        // the same path would otherwise be invisible here. See
        // `REGISTRATION_DATE_PRECONDITION_CODES` for the boundary this enforces.
        if (fieldKey === 'registrationDate' && REGISTRATION_DATE_PRECONDITION_CODES.has(issue.code)) {
            registrationDatePreconditionUnmet = true;
        }

        // First issue per field wins — Zod can report several for one path and the
        // field has room for one line. `??=` keeps that deterministic without a
        // separate `has` check.
        fields[fieldKey] ??= message;
    }

    return { fieldIssues: fields, formIssues: form, registrationDateBlocked: registrationDatePreconditionUnmet };
}
